import React, { useState, useEffect, useCallback, useMemo, useDeferredValue } from 'react';
import MaterialSelectorPanel from './MaterialSelectorPanel';
import AddAccessoriesDialog from './AddAccessoriesDialog';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import * as materialGroupApi from '../api/materialGroupApi';
import * as departmentApi from '../api/departmentApi';
import * as materialAccessoryApi from '../api/materialAccessoryApi';
import { getCached } from '../utils/apiCache';
import { matchesSearch } from '../utils/searchFilter';
import * as accessoryExcelApi from '../api/accessoryExcelApi';
import { useSnackbar } from '../context/SnackbarContext';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmDialog from '../components/common/ConfirmDialog';

export default function MaterialAccessories() {
  // ─── Section 1: Material Selection ────────────────────────────────────────
  // Search / filter state AND the material list itself now live inside
  // MaterialSelectorPanel (its own debounced server search) — not here.
  // Only the small bounded reference lists (groups/departments) stay here,
  // since MaterialSelectorPanel needs them for its dept/group dropdowns.
  const [materialGroups, setMaterialGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  // ─── Section 2: Base Accessories (type-independent) ──────────────────────
  const [baseAccessories, setBaseAccessories] = useState([]);
  const [baseLoading, setBaseLoading] = useState(false);
  const [isBaseEditing, setIsBaseEditing] = useState(false);
  const [baseToDelete, setBaseToDelete] = useState(null);

  // ─── Section 3: Type of Installation Selection ───────────────────────────
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [typesLoading, setTypesLoading] = useState(false);

  // ─── Section 4: Accessories Display & Management ──────────────────────────
  const [linkedAccessories, setLinkedAccessories] = useState([]);
  const [rightLoading, setRightLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  // ─── Add Accessories Dialog ───────────────────────────────────────────────
  // Search/filter/selection state lives inside AddAccessoriesDialog (React.memo).
  // Only open/mode are kept here so the parent can trigger the dialog.
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogMode, setAddDialogMode] = useState('type'); // 'base' | 'type'

  // ─── Download & Selection ─────────────────────────────────────────────────
  const [downloading, setDownloading] = useState(false);
  const [selectedDownloadCombinations, setSelectedDownloadCombinations] = useState([]); // [{materialId, typeId, material, materialGroup, type}]
  const [downloadSearch, setDownloadSearch] = useState('');
  const [downloadDeptFilter, setDownloadDeptFilter] = useState('');
  const [downloadGroupFilter, setDownloadGroupFilter] = useState('');
  const [downloadFocusedMaterial, setDownloadFocusedMaterial] = useState(null);

  // Materials that have at least one linked accessory (base or type-specific),
  // already hydrated with the fields the Download Selection panel renders —
  // fetched directly, never cross-referenced against a full Material list.
  // null = still loading; empty array = loaded but nothing linked yet.
  const [linkedMaterials, setLinkedMaterials] = useState(null);

  // Set of typeOfInstallation IDs that have accessories for the focused download material.
  // null = no material focused (or still loading); Set = loaded.
  const [focusedMaterialTypeIds, setFocusedMaterialTypeIds] = useState(null);

  const { showSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('MANAGE_MATERIAL_ACCESSORIES');
  const canView = hasPermission('VIEW_MATERIAL_ACCESSORIES') || canManage || hasPermission('DOWNLOAD_MATERIAL_ACCESSORIES');
  const canDownload = hasPermission('DOWNLOAD_MATERIAL_ACCESSORIES') || canManage;

  // ── Deferred download search ──────────────────────────────────────────────────
  // Material search state (and its deferred value) now lives in MaterialSelectorPanel.
  // Only the download section search remains here.
  const deferredDownloadSearch = useDeferredValue(downloadSearch);

  // Load reference data (departments, material groups, installation types —
  // all small and bounded regardless of how many materials exist) plus the
  // hydrated linked-materials list for the Download Selection filter.
  useEffect(() => {
    const loadData = async () => {
      try {
        const [depts, mGroups, types, linkedRes] = await Promise.all([
          getCached('departments:all', () => departmentApi.getAll()),
          getCached('materialGroups:all', () => materialGroupApi.getAll()),
          getCached('typesOfInstallation:all', () => materialAccessoryApi.getTypesOfInstallation()),
          materialAccessoryApi.getLinkedMaterials(),
        ]);
        setDepartments(Array.isArray(depts) ? depts : (depts.data || []));
        setMaterialGroups(Array.isArray(mGroups) ? mGroups : (mGroups.data || []));
        setTypes(Array.isArray(types) ? types : (types.data || []));
        setLinkedMaterials(linkedRes.data ?? linkedRes ?? []);
      } catch (e) {
        showSnackbar(e.message, 'error');
        setLinkedMaterials([]); // fail-open: show empty rather than crash
      }
    };
    loadData();
  }, [showSnackbar]);

  // When a material is focused in the Download panel, load which types have accessories for it.
  // Only those types are shown in the type-toggle column (avoids selecting empty combos).
  useEffect(() => {
    if (!downloadFocusedMaterial) {
      setFocusedMaterialTypeIds(null);
      return;
    }
    materialAccessoryApi.getByMaterial(downloadFocusedMaterial.id)
      .then((res) => {
        const groups = res.data ?? res ?? [];
        setFocusedMaterialTypeIds(new Set(groups.map((g) => g.typeOfInstallation.id)));
      })
      .catch(() => setFocusedMaterialTypeIds(new Set()));
  }, [downloadFocusedMaterial]);

  // Accessory fetching for the dialog is now handled inside AddAccessoriesDialog.


  // Load base accessories when material changes
  useEffect(() => {
    if (!selectedMaterial) {
      setBaseAccessories([]);
      return;
    }
    const loadBase = async () => {
      setBaseLoading(true);
      try {
        const res = await materialAccessoryApi.getBaseAccessories(selectedMaterial.id);
        const list = res.data || res || [];
        // The API returns MaterialBaseAccessory rows with an `accessory` relation
        setBaseAccessories(list.map((row) => row.accessory ?? row));
      } catch (e) {
        showSnackbar(e.message, 'error');
        setBaseAccessories([]);
      } finally {
        setBaseLoading(false);
      }
    };
    loadBase();
  }, [selectedMaterial, showSnackbar]);

  // Load type-specific accessories for selected material + type
  useEffect(() => {
    if (!selectedMaterial || !selectedType) {
      setLinkedAccessories([]);
      return;
    }

    const loadAccessories = async () => {
      setRightLoading(true);
      try {
        const res = await materialAccessoryApi.getForMaterialAndType(selectedMaterial.id, selectedType.id);
        setLinkedAccessories(res.data || res || []);
      } catch (e) {
        showSnackbar(e.message, 'error');
        setLinkedAccessories([]);
      } finally {
        setRightLoading(false);
      }
    };

    loadAccessories();
  }, [selectedMaterial, selectedType, showSnackbar]);

  // matGroupCodes / filteredMaterials / visibleMaterials moved to MaterialSelectorPanel

  // Open the Add Accessories dialog in the given mode
  const openAddDialog = (mode = 'type') => {
    if (!selectedMaterial) { showSnackbar('Select a material first', 'error'); return; }
    if (mode === 'type' && !selectedType) { showSnackbar('Select an installation type first', 'error'); return; }
    setAddDialogMode(mode);
    setAddDialogOpen(true);
  };

  // Handle delete base accessory
  const handleDeleteBaseAccessory = async () => {
    setBaseLoading(true);
    try {
      await materialAccessoryApi.removeBaseAccessories(selectedMaterial.id, [baseToDelete.id]);
      showSnackbar('Base accessory removed', 'success');
      setBaseAccessories((prev) => prev.filter((a) => a.id !== baseToDelete.id));
      setBaseToDelete(null);
    } catch (e) {
      showSnackbar(e.message, 'error');
    } finally {
      setBaseLoading(false);
    }
  };

  // Handle delete type-specific accessory
  const handleDeleteAccessory = async () => {
    setRightLoading(true);
    try {
      await materialAccessoryApi.removeFromMaterial(
        selectedMaterial.id,
        selectedType.id,
        [toDelete.id]
      );
      showSnackbar('Accessory removed', 'success');
      setLinkedAccessories((prev) => prev.filter((a) => a.id !== toDelete.id));
      setToDelete(null);
    } catch (e) {
      showSnackbar(e.message, 'error');
    } finally {
      setRightLoading(false);
    }
  };

  // Handle download (selected combinations or all)
  const handleDownloadSelected = async () => {
    if (selectedDownloadCombinations.length === 0) {
      showSnackbar('Select at least one Material+Type combination', 'error');
      return;
    }
    setDownloading(true);
    try {
      const combos = selectedDownloadCombinations.map((item) => ({
        materialId: item.materialId,
        typeOfInstallationId: item.typeId,
      }));
      await accessoryExcelApi.downloadMapping(undefined, combos);
      showSnackbar('Download started', 'success');
    } catch (e) {
      showSnackbar(e.message, 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      // Download all (no filters)
      await accessoryExcelApi.downloadMapping();
      showSnackbar('Download started', 'success');
    } catch (e) {
      showSnackbar(e.message, 'error');
    } finally {
      setDownloading(false);
    }
  };

  // Toggle a Material+Type combo in/out of download selection
  const toggleDownloadCombo = (material, type) => {
    const exists = selectedDownloadCombinations.some(
      (item) => item.materialId === material.id && item.typeId === type.id
    );
    if (exists) {
      setSelectedDownloadCombinations((prev) =>
        prev.filter((item) => !(item.materialId === material.id && item.typeId === type.id))
      );
    } else {
      setSelectedDownloadCombinations((prev) => [
        ...prev,
        {
          materialId: material.id,
          typeId: type.id,
          material: material.name,
          materialGroup: material.materialGroup.groupCode,
          type: type.name,
        },
      ]);
    }
  };

  // Remove from download selection
  const removeFromDownloadSelection = (materialId, typeId) => {
    setSelectedDownloadCombinations((prev) =>
      prev.filter((item) => !(item.materialId === materialId && item.typeId === typeId))
    );
  };

  // Clear all selections
  const clearDownloadSelection = () => {
    setSelectedDownloadCombinations([]);
  };

  // Stable callback passed to MaterialSelectorPanel.
  // useCallback with no deps because all setters from useState are already stable.
  // This prevents React.memo on the panel from seeing a new function reference on
  // every parent render, which would defeat the memo entirely.
  const handleMaterialSelect = useCallback((mat) => {
    setSelectedMaterial(mat);
    setSelectedType(null);
    setIsEditing(false);
    setIsBaseEditing(false);
  }, []);

  // ── All derived values memoized — none re-run on every render ────────────────

  const activeTypes = useMemo(
    () => types.filter((t) => t.isActive),
    [types]
  );

  // For the Download panel's type-toggle column: only types that have accessories
  // linked to the currently focused material.  While loading (null) fall back to
  // activeTypes so nothing disappears mid-render; once resolved the Set filters it.
  const typesForDownload = useMemo(() => {
    if (!focusedMaterialTypeIds) return [];
    return activeTypes.filter((t) => focusedMaterialTypeIds.has(t.id));
  }, [activeTypes, focusedMaterialTypeIds]);

  // Set rebuilt only when selectedDownloadCombinations changes, not on every keystroke
  const selectedMatTypeKeys = useMemo(
    () => new Set(selectedDownloadCombinations.map((item) => `${item.materialId}|${item.typeId}`)),
    [selectedDownloadCombinations]
  );

  // Download section uses deferredDownloadSearch — same pattern as material search above.
  // linkedMaterials is already the bounded "has ≥1 linked accessory" set,
  // fetched hydrated directly from the server — no cross-referencing against
  // a full Material list needed.
  const filteredDownloadMaterials = useMemo(() => (linkedMaterials || []).filter((m) => {
    if (downloadDeptFilter && m.materialGroup.departmentId !== Number(downloadDeptFilter)) return false;
    if (downloadGroupFilter && m.materialGroup.groupCode !== downloadGroupFilter) return false;
    if (deferredDownloadSearch &&
        !matchesSearch(m.name, deferredDownloadSearch) &&
        !matchesSearch(m.materialGroup.groupCode, deferredDownloadSearch)) return false;
    return true;
  }), [linkedMaterials, downloadDeptFilter, downloadGroupFilter, deferredDownloadSearch]);

  const effectiveFocusedMaterial = useMemo(() => {
    if (!downloadFocusedMaterial) return null;
    return filteredDownloadMaterials.some((m) => m.id === downloadFocusedMaterial.id)
      ? downloadFocusedMaterial
      : null;
  }, [downloadFocusedMaterial, filteredDownloadMaterials]);

  const availableGroupCodes = useMemo(() => Array.from(
    new Set(
      (linkedMaterials || [])
        .filter((m) => !downloadDeptFilter || m.materialGroup.departmentId === Number(downloadDeptFilter))
        .map((m) => m.materialGroup.groupCode)
    )
  ).sort(), [linkedMaterials, downloadDeptFilter]);

  // dialogLinkedIds / dialogSourceList / dialogGroupCodes / dialogAccessories
  // moved into AddAccessoriesDialog component.

  if (!canView) {
    return (
      <Box>
        <PageHeader title="Material-Accessories" />
        <Alert severity="warning">You don't have permission to view material-accessories mapping.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Material-Accessories Dependency Mapping" />

      <Card>
        <CardHeader
          title="Link Accessories to Materials by Installation Type"
          subheader="Select a material, choose its installation type, then manage accessories for that specific combination"
        />
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* ─── SECTION 1: MATERIALS ────────────────────────────────────────────────────────── */}
            {/* Search/filter state lives inside MaterialSelectorPanel (React.memo).          */}
            {/* Typing only re-renders that small component — not this entire page.           */}
            <Grid item xs={12} sm={6} md={3}>
              <MaterialSelectorPanel
                groups={materialGroups}
                departments={departments}
                selectedMaterial={selectedMaterial}
                onSelect={handleMaterialSelect}
              />
            </Grid>

            {/* ─── SECTION 2: BASE ACCESSORIES ──────────────────────────────────────────────────── */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={baseAccessories.length > 0 ? 2 : 0}
                sx={{
                  p: 1.5,
                  bgcolor: baseAccessories.length > 0 ? '#E3F2FD' : '#FAFAFA',
                  border: '2px solid',
                  borderColor: baseAccessories.length > 0 ? '#1565C0' : '#E0E0E0',
                  transition: 'all 0.2s',
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1565C0', fontSize: '0.8rem', letterSpacing: 0.3, textTransform: 'uppercase' }}>
                    2. Base Accessories{selectedMaterial && baseAccessories.length > 0 ? ` (${baseAccessories.length})` : ''}
                  </Typography>
                  {canManage && selectedMaterial && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => openAddDialog('base')}
                      variant="text"
                      sx={{ color: '#1565C0' }}
                    >
                      Edit
                    </Button>
                  )}
                </Box>

                {!selectedMaterial ? (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Select a material first
                  </Alert>
                ) : baseLoading ? (
                  <LoadingSpinner />
                ) : baseAccessories.length === 0 ? (
                  <Typography sx={{ color: '#999', p: 2, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.875rem' }}>
                    No base accessories linked
                  </Typography>
                ) : (
                  <List dense sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid #BBDEFB', borderRadius: 1 }}>
                    {baseAccessories.map((acc) => (
                      <ListItemButton
                        key={acc.id}
                        disableRipple
                        sx={{ borderBottom: '1px solid #f0f0f0', py: 0.5 }}
                      >
                        <ListItemText
                          primary={acc.name}
                          secondary={`Unit: ${acc.unit}`}
                          primaryTypographyProps={{ style: { fontWeight: 500, fontSize: '0.82rem' } }}
                          secondaryTypographyProps={{ style: { fontSize: '0.72rem' } }}
                        />
                        {canManage && (
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => setBaseToDelete(acc)}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        )}
                      </ListItemButton>
                    ))}
                  </List>
                )}

              </Paper>
            </Grid>

            {/* ─── SECTION 3: TYPE OF INSTALLATION ──────────────────────────────────────────────── */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={selectedType ? 2 : 0}
                sx={{
                  p: 1.5,
                  bgcolor: selectedType ? '#FFF3E0' : '#FAFAFA',
                  border: '2px solid',
                  borderColor: selectedType ? '#FF6F00' : '#E0E0E0',
                  transition: 'all 0.2s',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 700, color: '#FF6F00', fontSize: '0.8rem', letterSpacing: 0.3, textTransform: 'uppercase' }}>
                  3. Select Installation Type
                </Typography>

                {!selectedMaterial ? (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Select a material first
                  </Alert>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel>Type of Installation</InputLabel>
                    <Select
                      value={selectedType?.id || ''}
                      onChange={(e) => {
                        const type = types.find((t) => t.id === e.target.value);
                        setSelectedType(type || null);
                      }}
                      label="Type of Installation"
                    >
                      <MenuItem value="">--- Select a type ---</MenuItem>
                      {types.filter((t) => t.isActive).map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.name}
                          {t.description && ` — ${t.description}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {selectedType && (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#FFF8E1', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, color: '#F57F17', display: 'block', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                      Selected Type:
                    </Typography>
                    <Chip
                      label={selectedType.name}
                      color="warning"
                      variant="filled"
                      sx={{ fontWeight: 600 }}
                    />
                    {selectedType.description && (
                      <Typography variant="caption" display="block" sx={{ mt: 1, color: '#555' }}>
                        {selectedType.description}
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* ─── SECTION 4: TYPE-SPECIFIC ACCESSORIES ────────────────────────────────────────── */}
            <Grid item xs={12} sm={12} md={3}>
              <Paper
                elevation={selectedType && linkedAccessories.length > 0 ? 2 : 0}
                sx={{
                  p: 1.5,
                  bgcolor: linkedAccessories.length > 0 ? '#E8F5E9' : '#FAFAFA',
                  border: '2px solid',
                  borderColor: linkedAccessories.length > 0 ? '#2E7D32' : '#E0E0E0',
                  transition: 'all 0.2s',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2E7D32', fontSize: '0.8rem', letterSpacing: 0.3, textTransform: 'uppercase' }}>
                    4. Type Accessories{selectedType && linkedAccessories.length > 0 ? ` (${linkedAccessories.length})` : ''}
                  </Typography>
                  {canManage && selectedType && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => openAddDialog('type')}
                      variant="text"
                      sx={{ color: '#2E7D32' }}
                    >
                      Edit
                    </Button>
                  )}
                </Box>

                {!selectedType ? (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Select material and type first
                  </Alert>
                ) : rightLoading ? (
                  <LoadingSpinner />
                ) : linkedAccessories.length === 0 ? (
                  <Typography sx={{ color: '#999', p: 2, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    No accessories linked yet
                  </Typography>
                ) : (
                  <List dense sx={{ minHeight: 240, maxHeight: 360, overflow: 'auto', border: '1px solid #E0E0E0', borderRadius: 1 }}>
                    {linkedAccessories.map((acc) => (
                      <ListItemButton
                        key={acc.id}
                        disableRipple
                        sx={{ borderBottom: '1px solid #f0f0f0', py: 0.5 }}
                      >
                        <ListItemText
                          primary={acc.name}
                          secondary={`Unit: ${acc.unit}`}
                          primaryTypographyProps={{ style: { fontWeight: 500, fontSize: '0.82rem' } }}
                          secondaryTypographyProps={{ style: { fontSize: '0.72rem' } }}
                        />
                        {canManage && (
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => setToDelete(acc)}
                              size="small"
                              title="Remove"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        )}
                      </ListItemButton>
                    ))}
                  </List>
                )}

              </Paper>
            </Grid>
          </Grid>

          {/* ─── DOWNLOAD SELECTION PANEL ───────────────────────────────────────────────── */}
          {canDownload && (
            <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid #E0E0E0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#424242', fontSize: '0.8rem', letterSpacing: 0.3, textTransform: 'uppercase' }}>
                  Download Selection
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {selectedDownloadCombinations.length > 0 && (
                    <>
                      <Chip
                        label={`${selectedDownloadCombinations.length} selected`}
                        size="small"
                        color="primary"
                        variant="filled"
                      />
                      <Button
                        size="small"
                        variant="text"
                        onClick={clearDownloadSelection}
                        sx={{ color: '#C62828', minWidth: 0 }}
                      >
                        Clear
                      </Button>
                    </>
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadSelected}
                    disabled={downloading || selectedDownloadCombinations.length === 0}
                    sx={{ backgroundColor: '#6A1B9A', '&:hover': { backgroundColor: '#4A0E7A' } }}
                  >
                    Download Selected
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadAll}
                    disabled={downloading}
                    sx={{ color: '#2E7D32', borderColor: '#2E7D32' }}
                  >
                    Download All
                  </Button>
                </Box>
              </Box>

              <Grid container spacing={2}>
                {/* LEFT: Filters + Material List */}
                <Grid item xs={12} md={5}>
                  <Paper elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: 1, overflow: 'hidden' }}>
                    {/* Filters */}
                    <Box sx={{ p: 2, bgcolor: '#FAFAFA', borderBottom: '1px solid #E0E0E0' }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by name or code..."
                        value={downloadSearch}
                        onChange={(e) => setDownloadSearch(e.target.value)}
                        InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, color: '#aaa', fontSize: 18 }} /> }}
                        sx={{ mb: 1.5 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <FormControl size="small" sx={{ flex: 1 }}>
                          <InputLabel>Department</InputLabel>
                          <Select
                            value={downloadDeptFilter}
                            onChange={(e) => { setDownloadDeptFilter(e.target.value); setDownloadGroupFilter(''); }}
                            label="Department"
                          >
                            <MenuItem value="">All</MenuItem>
                            {departments.map((d) => (
                              <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ flex: 1 }}>
                          <InputLabel>Group Code</InputLabel>
                          <Select
                            value={downloadGroupFilter}
                            onChange={(e) => setDownloadGroupFilter(e.target.value)}
                            label="Group Code"
                          >
                            <MenuItem value="">All</MenuItem>
                            {availableGroupCodes.map((code) => (
                              <MenuItem key={code} value={code}>{code}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    {/* Material list */}
                    <Typography variant="caption" sx={{ display: 'block', px: 2, pt: 1.5, pb: 0.5, color: '#888' }}>
                      {filteredDownloadMaterials.length} material{filteredDownloadMaterials.length !== 1 ? 's' : ''} found
                    </Typography>
                    <List dense disablePadding sx={{ maxHeight: 350, overflow: 'auto' }}>
                      {filteredDownloadMaterials.length === 0 ? (
                        <Typography sx={{ p: 2, color: '#bbb', textAlign: 'center', fontSize: '0.875rem' }}>
                          No materials match
                        </Typography>
                      ) : (
                        filteredDownloadMaterials.map((mat) => {
                          const isFocused = downloadFocusedMaterial?.id === mat.id;
                          const selCount = activeTypes.filter((t) =>
                            selectedMatTypeKeys.has(`${mat.id}|${t.id}`)
                          ).length;
                          return (
                            <ListItemButton
                              key={mat.id}
                              selected={isFocused}
                              onClick={() => setDownloadFocusedMaterial(isFocused ? null : mat)}
                              sx={{
                                borderBottom: '1px solid #f5f5f5',
                                '&.Mui-selected': {
                                  bgcolor: '#DBEAFE',
                                  boxShadow: 'inset 3px 0 0 #1D4ED8',
                                  '&:hover': { bgcolor: '#BFDBFE' },
                                },
                                '&:hover': { bgcolor: '#F0F7FF' },
                              }}
                            >
                              <ListItemText
                                primary={mat.name}
                                secondary={mat.materialGroup.groupCode}
                                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isFocused ? 700 : 400, color: isFocused ? '#1E3A8A' : undefined }}
                                secondaryTypographyProps={{ fontSize: '0.75rem', color: isFocused ? '#2563EB' : undefined }}
                              />
                              {selCount > 0 && (
                                <Chip
                                  label={selCount}
                                  size="small"
                                  color="primary"
                                  variant="filled"
                                  sx={{ ml: 1, height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                                />
                              )}
                            </ListItemButton>
                          );
                        })
                      )}
                    </List>
                  </Paper>
                </Grid>

                {/* RIGHT: Type toggles for focused material */}
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: 1, height: '100%', minHeight: 200 }}>
                    {!effectiveFocusedMaterial ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                        <Typography sx={{ color: '#bbb', fontSize: '0.875rem', textAlign: 'center', px: 3 }}>
                          Select a material on the left to see installation types
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#333' }}>
                          {effectiveFocusedMaterial.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 2 }}>
                          {effectiveFocusedMaterial.materialGroup.groupCode}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {typesForDownload.length === 0 ? (
                          <Typography sx={{ color: '#bbb', fontSize: '0.82rem', textAlign: 'center', mt: 2 }}>
                            No installation types with accessories
                          </Typography>
                        ) : (
                          <>
                            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1.5 }}>
                              Click to select / unselect
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {typesForDownload.map((type) => {
                                const key = `${effectiveFocusedMaterial.id}|${type.id}`;
                                const isOn = selectedMatTypeKeys.has(key);
                                return (
                                  <Button
                                    key={type.id}
                                    fullWidth
                                    size="small"
                                    variant={isOn ? 'contained' : 'outlined'}
                                    onClick={() => toggleDownloadCombo(effectiveFocusedMaterial, type)}
                                    sx={{
                                      justifyContent: 'flex-start',
                                      backgroundColor: isOn ? '#FF6F00' : 'transparent',
                                      borderColor: '#FF6F00',
                                      color: isOn ? '#fff' : '#FF6F00',
                                      fontWeight: isOn ? 600 : 400,
                                      '&:hover': {
                                        backgroundColor: isOn ? '#E65100' : '#FFF8E1',
                                        borderColor: '#FF6F00',
                                      },
                                    }}
                                  >
                                    {isOn ? '✓ ' : ''}{type.name}
                                  </Button>
                                );
                              })}
                            </Box>
                          </>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* SELECTED COMBINATIONS */}
                <Grid item xs={12} md={3}>
                  <Paper elevation={0} sx={{ border: '1px solid #C8E6C9', borderRadius: 1, height: '100%', minHeight: 200, bgcolor: '#F9FBE7' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #C8E6C9' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                        Selected ({selectedDownloadCombinations.length})
                      </Typography>
                    </Box>
                    {selectedDownloadCombinations.length === 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 45px)', minHeight: 120 }}>
                        <Typography sx={{ color: '#bbb', fontSize: '0.8rem', textAlign: 'center', px: 2 }}>
                          None selected
                        </Typography>
                      </Box>
                    ) : (
                      <List dense disablePadding sx={{ maxHeight: 350, overflow: 'auto' }}>
                        {selectedDownloadCombinations.map((item) => (
                          <Box
                            key={`${item.materialId}|${item.typeId}`}
                            sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75, borderBottom: '1px solid #E8F5E9' }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {item.material}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#FF6F00', fontSize: '0.7rem' }}>
                                {item.type}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => removeFromDownloadSelection(item.materialId, item.typeId)}
                              sx={{ ml: 0.5, color: '#bbb', '&:hover': { color: '#C62828' } }}
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        ))}
                      </List>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ─── Add Accessories Dialog ──────────────────────────────────────────── */}
      {/* All search/filter/selection state is isolated inside AddAccessoriesDialog  */}
      {/* (React.memo). Typing in the dialog only re-renders that component.         */}
      <AddAccessoriesDialog
        open={addDialogOpen}
        mode={addDialogMode}
        selectedMaterial={selectedMaterial}
        selectedType={selectedType}
        baseAccessories={baseAccessories}
        linkedAccessories={linkedAccessories}
        departments={departments}
        onClose={() => setAddDialogOpen(false)}
        onBaseAdded={(list) => { setBaseAccessories(list); setAddDialogOpen(false); }}
        onTypeAdded={(list) => { setLinkedAccessories(list); setAddDialogOpen(false); }}
      />

      {/* ─── Delete Type-Specific Accessory Dialog ────────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!toDelete}
        title="Remove Accessory"
        message={`Remove "${toDelete?.name}" from ${selectedMaterial?.name} (${selectedType?.name})?`}
        onConfirm={handleDeleteAccessory}
        onCancel={() => setToDelete(null)}
      />

      {/* ─── Delete Base Accessory Dialog ─────────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!baseToDelete}
        title="Remove Base Accessory"
        message={`Remove "${baseToDelete?.name}" from the base accessories of "${selectedMaterial?.name}"?`}
        onConfirm={handleDeleteBaseAccessory}
        onCancel={() => setBaseToDelete(null)}
      />
    </Box>
  );
}
